import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, FlatList } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ConsumerLookupInfo } from "../../../../../view-models/ConsumerLookupInfo";
import { billerSearch } from "../../../../../services/billpay.service";

interface PreviosBillerInfoStepProps {
  onSelectBiller?: (biller: any) => void;
}

const PreviosBillerInfoStep: React.FC<PreviosBillerInfoStepProps> = ({ onSelectBiller }) => {
  const billPayAuth = useSessionStore(
    (s: SessionState) => (s as any).billPayGetAuthConsumerResponseStore
  );
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setSelectedBillerInformationStore = useSessionStore((s: SessionState) => (s as any).setSelectedBillerInformationStore);
  const setBillPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerAccountNumberStore);
  const setBillPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerSearchResponseStore);
  const billPayAuthConsumerResponse = useSessionStore((s: SessionState) => (s as any).billPayGetAuthConsumerResponseStore);

  const [query, setQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | number | null>(null);
  const [searchResultRaw, setSearchResultRaw] = useState<any | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const allBillers: any[] = useMemo(() => {
    const src = (billPayAuth?.ConsumerInfo?.BillerLookupInfos) as any;
    if (Array.isArray(src)) return src;
    if (src && Array.isArray(src.BillerLookupInfos)) return src.BillerLookupInfos;
    return [];
  }, [billPayAuth]);

  // Normalize previous billers from auth
  const normalizedBillers = useMemo(() => {
    return allBillers.map((item, idx) => {
      const id = item?.BillerId ?? item?.billerId ?? item?.Id ?? item?.id ?? idx;
      const name = item?.BillerName ?? item?.billerName ?? String(item?.name ?? id);
      return { key: id, name, raw: item };
    });
  }, [allBillers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalizedBillers;
    return normalizedBillers.filter(b => b.name?.toLowerCase().includes(q));
  }, [normalizedBillers, query]);

  // Fire billerSearch when query length >= 2
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (q.length >= 2 && agentId && unitProfileId) {
      setSearchLoading(true);
      setSearchError(null);
      // debounce minimal via setTimeout
      const t = setTimeout(() => {
        billerSearch(q, String(agentId), String(unitProfileId), 'NAME')
          .then((res) => {
            if (cancelled) return;
            setSearchResultRaw(res);
            try { setBillPayBillerSearchResponseStore?.(res); } catch {}
          })
          .catch((err) => {
            if (cancelled) return;
            setSearchError(err?.message || 'Search failed');
            setSearchResultRaw(null);
          })
          .finally(() => { if (!cancelled) setSearchLoading(false); });
      }, 350);
      return () => { cancelled = true; clearTimeout(t); };
    } else {
      setSearchResultRaw(null);
      setSearchLoading(false);
      setSearchError(null);
    }
    return () => { cancelled = true; };
  }, [query, agentId, unitProfileId]);

  // Normalize search results
  const normalizedSearchResults = useMemo(() => {
    const s: any = searchResultRaw as any;
    if (!s) return [] as Array<{ key: string | number; name: string; raw: any }>;
    const listCandidate: any = Array.isArray(s)
      ? s
      : (s.BillerInfos ?? s.billerInfos ?? s.BillerLookupInfos ?? s.billerLookupInfos ?? s.Items ?? s.items ?? s.Result ?? s.result ?? []);
    const list: any[] = Array.isArray(listCandidate)
      ? listCandidate
      : (Array.isArray(listCandidate?.Items) ? listCandidate.Items : []);
    return list.map((item: any, idx: number) => {
      const id = item?.BillerId ?? item?.billerId ?? item?.Id ?? item?.id ?? idx;
      const name = item?.BillerName ?? item?.billerName ?? String(item?.name ?? id);
      return { key: id, name, raw: item };
    });
  }, [searchResultRaw]);

  const handleSelect = (item: { key: string | number; name: string; raw: any }) => {
    setSelectedKey(item.key);
    try { setSelectedBillerInformationStore?.(item.raw); } catch {}
    // Determine receiveCode source
    const raw: any = item.raw || {};
    let receiveCode: string | undefined = undefined;
    // Previous billers from AuthConsumer/ConsumerLookup
    receiveCode = raw?.ReceiveCode ?? raw?.receiveCode ?? receiveCode;
    // If an account number is present on the selected biller, store it for prefill
    const acct = raw?.BillerAccountNumber ?? raw?.billerAccountNumber ?? null;
    try { setBillPayBillerAccountNumberStore?.(acct ? String(acct) : null); } catch {}
    // If no receiveCode yet and selection came from search results, still try same keys
    if (!receiveCode && searchResultRaw) {
      receiveCode = raw?.ReceiveCode ?? raw?.receiveCode;
    }
    // Fire CODE search to resolve full/current biller info context
    try {
      if (agentId && unitProfileId) {
        billerSearch(String(item.name || ''), String(agentId), String(unitProfileId), 'CODE', receiveCode)
          .then((res) => { try { setBillPayBillerSearchResponseStore?.(res); } catch {} })
          .catch(() => {});
      }
    } catch {}
    onSelectBiller?.(item.raw);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Search for a company to make a payment</Text>
      <TextInput
        placeholder="Search"
        value={query}
        onChangeText={setQuery}
        style={styles.searchInput}
        placeholderTextColor="#9CA3AF"
      />

      {query.trim().length >= 2 ? (
        normalizedSearchResults.length > 0 ? (
          <>
            <Text style={styles.sectionHeader}>Results</Text>
            <FlatList
              data={normalizedSearchResults}
              keyExtractor={(item) => String(item.key)}
              renderItem={({ item }) => (
                <TouchableOpacity style={styles.row} onPress={() => handleSelect(item)}>
                  <View style={[styles.radioOuter, selectedKey === item.key && styles.radioOuterActive]}>
                    {selectedKey === item.key ? <View style={styles.radioInner} /> : null}
                  </View>
                  <Text style={styles.rowText}>{item.name}</Text>
                </TouchableOpacity>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              contentContainerStyle={{ paddingBottom: 12 }}
            />
          </>
        ) : (
          <View style={{ marginTop: 24 }}>
            <Text style={styles.emptyText}>{searchLoading ? 'Searching…' : (searchError || 'No billers found.')}</Text>
          </View>
        )
      ) : filtered.length > 0 ? (
        <>
          <Text style={styles.sectionHeader}>Recent</Text>
          <FlatList
            data={filtered}
            keyExtractor={(item) => String(item.key)}
            renderItem={({ item }) => (
              <TouchableOpacity style={styles.row} onPress={() => handleSelect(item)}>
                <View style={[styles.radioOuter, selectedKey === item.key && styles.radioOuterActive]}>
                  {selectedKey === item.key ? <View style={styles.radioInner} /> : null}
                </View>
                <Text style={styles.rowText}>{item.name}</Text>
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            contentContainerStyle={{ paddingBottom: 12 }}
          />
        </>
      ) : (
        <View style={{ marginTop: 24 }}>
          <Text style={styles.emptyText}>No billers found.</Text>
        </View>
      )}
    </View>
  );
};

export default PreviosBillerInfoStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 8,
    backgroundColor: "#FFF"
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    color: "#111",
    marginBottom: 16
  },
  searchInput: {
    width: "100%",
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    color: "#111"
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 8,
    fontSize: 16,
    fontWeight: "700",
    color: "#111"
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12
  },
  rowText: {
    marginLeft: 12,
    fontSize: 16,
    color: "#111"
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: "#9CA3AF",
    alignItems: "center",
    justifyContent: "center"
  },
  radioOuterActive: {
    borderColor: "#111"
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#111"
  },
  separator: {
    height: 1,
    backgroundColor: "#E5E7EB"
  },
  emptyText: {
    fontSize: 14,
    color: "#6B7280"
  }
});


